/**
 * Client-side CSV export (no third-party deps — admin-v2 ships no exceljs/file-saver, and the
 * "no new deps" constraint forbids adding them). Produces a UTF-8 file with a BOM and a `;`
 * delimiter so Excel (incl. the el-GR locale, whose default list separator is `;`) opens it
 * directly with Greek text intact. The `.csv` opens natively in Excel.
 */
export interface CsvColumn<T> {
	header: string;
	value: (row: T) => string | number | null | undefined;
}

const DELIMITER = ';';

function escapeCell(value: string | number | null | undefined): string {
	let s = value == null ? '' : String(value);
	// A leading =, +, -, @ or tab makes Excel evaluate the cell as a formula (CSV injection) —
	// free-text values (e.g. audited before/after values) must not become live =HYPERLINK(...) calls.
	// Numbers are exempt: they cannot carry a formula, and prefixing would corrupt negative values.
	if (typeof value === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
	return /["\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportToCsv<T>(filename: string, columns: CsvColumn<T>[], rows: T[]): void {
	const lines = [
		columns.map(c => escapeCell(c.header)).join(DELIMITER),
		...rows.map(row => columns.map(c => escapeCell(c.value(row))).join(DELIMITER))
	];
	// Prepend the UTF-8 BOM so Excel detects the encoding (Greek otherwise renders as mojibake).
	const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}
